import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, expand, Observable, reduce } from 'rxjs';
import {
  DisbursementCertificationListParams,
  DisbursementCertificationListResponse,
  DisbursementCertificationModel,
  DisbursementDocType,
  DisbursementRequestRevisionPayload,
  DisbursementRevisionCandidatesResponse,
  DisbursementRosterParams,
  DisbursementRosterResponse,
  DisbursementTestProvisionResponse,
} from '../../models/disbursement/disbursement.model';

// Disbursement Document Certification service endpoints. v2.0 phase 1 only (ADR-BU) —
// context/decisions/DECISION_DisbursementV20.md. Phase-2 round transitions/downloads live on DisbursementRoundService.
@Injectable({
  providedIn: 'root',
})
export class DisbursementCertificationService {
  private apiUrl = '/disbursement_certifications';

  #http = inject(HttpClient);

  getCertifications(
    params?: DisbursementCertificationListParams,
  ): Observable<DisbursementCertificationListResponse> {
    return this.#http.get<DisbursementCertificationListResponse>(this.apiUrl, {
      params: this.buildListParams(params),
    });
  }

  getCertification(id: number): Observable<DisbursementCertificationModel> {
    return this.#http.get<DisbursementCertificationModel>(`${this.apiUrl}/${id}`);
  }

  teacherCertify(id: number): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(`${this.apiUrl}/${id}/teacher_certify`, {});
  }

  // Phase 1's last station — pending_finance -> pending_round (the pool).
  financeCertify(id: number): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(`${this.apiUrl}/${id}/finance_certify`, {});
  }

  // Generalized send-back for either phase-1 actor (teacher, finance); dispatched server-side
  // on active role. Redesign: this IS the special-correction request now — `items`/
  // `new_course_request_note` come from the picker dialog fed by getRevisionCandidates.
  requestRevision(
    id: number,
    payload: DisbursementRequestRevisionPayload,
  ): Observable<DisbursementCertificationModel> {
    return this.#http.post<DisbursementCertificationModel>(`${this.apiUrl}/${id}/request_revision`, payload);
  }

  // Subject picker rows for the send-back dialog — same actor/gating as request_revision
  // itself. An international-course cert comes back `show_picker: false` (reason-only);
  // regular shared-workload rows remain selectable and are expanded by the API on submit.
  getRevisionCandidates(id: number): Observable<DisbursementRevisionCandidatesResponse> {
    return this.#http.get<DisbursementRevisionCandidatesResponse>(`${this.apiUrl}/${id}/revision_candidates`);
  }

  // Persisted final for completed cert; 404 until the cert's documents are generated.
  // For international certs: per-teacher doc instances (doc_3/doc_7) selected by teacherUserId.
  downloadDocument(id: number, docType: DisbursementDocType, teacherUserId?: number | null): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/${id}/documents/${docType}`, {
      params: this.teacherUserIdParams(teacherUserId),
      responseType: 'blob',
    });
  }

  downloadMergedDocument(id: number): Observable<Blob> {
    return this.downloadDocument(id, 'merged');
  }

  // On-the-fly per-doc preview; rendered now, not persisted.
  previewDocument(id: number, docType: DisbursementDocType, teacherUserId?: number | null): Observable<Blob> {
    return this.#http.get(`${this.apiUrl}/${id}/documents/${docType}/preview`, {
      params: this.teacherUserIdParams(teacherUserId),
      responseType: 'blob',
    });
  }

  private teacherUserIdParams(teacherUserId?: number | null): HttpParams {
    let params = new HttpParams();
    if (teacherUserId != null) {
      params = params.set('teacher_user_id', String(teacherUserId));
    }

    return params;
  }

  // Walks every page and flattens result (required for per-dept aggregation from full term set, not page 1 only).
  getAllCertifications(
    params?: Omit<DisbursementCertificationListParams, 'page' | 'limit'>,
  ): Observable<DisbursementCertificationModel[]> {
    const pageSize = 200;

    const fetchPage = (page: number): Observable<DisbursementCertificationListResponse> => (
      this.getCertifications({ ...params, page, limit: pageSize })
    );

    const expandNextPage = (
      res: DisbursementCertificationListResponse,
    ): Observable<DisbursementCertificationListResponse> => {
      if (res.page < res.total_pages) {
        return fetchPage(res.page + 1);
      }

      return EMPTY;
    };

    const accumulatePages = (
      all: DisbursementCertificationModel[],
      res: DisbursementCertificationListResponse,
    ): DisbursementCertificationModel[] => [...all, ...res.data];

    return fetchPage(1).pipe(
      expand(expandNextPage),
      reduce(accumulatePages, [] as DisbursementCertificationModel[]),
    );
  }

  testProvision(academicTermId: number): Observable<DisbursementTestProvisionResponse> {
    return this.#http.post<DisbursementTestProvisionResponse>(`${this.apiUrl}/test_provision`, {
      academic_term_id: academicTermId,
    });
  }

  // Document officer's per-term roster (v2.0 phases — see DisbursementRosterPhase).
  getRoster(params: DisbursementRosterParams): Observable<DisbursementRosterResponse> {
    const httpParams = new HttpParams().set('academic_term_id', String(params.academic_term_id));
    return this.#http.get<DisbursementRosterResponse>(`${this.apiUrl}/roster`, { params: httpParams });
  }

  private buildListParams(params?: DisbursementCertificationListParams): HttpParams {
    let httpParams = new HttpParams();
    const supportedParams: (keyof DisbursementCertificationListParams)[] = [
      'academic_term_id',
      'status',
      'scope',
      'ownership',
      'department_id',
      'keywords',
      'keyword_scope',
      'page',
      'limit',
      'sort',
      'direction',
    ];

    supportedParams.forEach((key) => {
      const value = params?.[key];
      if (value != null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return httpParams;
  }
}
